// FILE: src/components/Register.js
import React, { useEffect, useMemo, useState } from "react";
import RegisterHeader from "./register/RegisterHeader";
import { clearManagerMode } from "../lib/managerMode";
import { auth } from "../firebase";
import { PLAN_CATALOG } from "../lib/planLimits";
import { fetchPlanCatalog, getPlanFromCatalog } from "../lib/planCatalogClient";
import { fetchSignInMethodsForEmail, createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

const FUNCTIONS_BASE =
  (typeof window !== "undefined" && window.__FN_BASE__) ||
  process.env.REACT_APP_FUNCTIONS_BASE_URL ||
  "/api";

function usePlanParams() {
  return useMemo(() => {
    const sp = new URLSearchParams(window.location.search || "");
    const fromLS = (k) => {
      try {
        return localStorage.getItem(k) || "";
      } catch {
        return "";
      }
    };
    const accountType = (sp.get("accountType") || "").trim().toLowerCase();
    const queryPrice = sp.get("price") || "";
    const plan = sp.get("plan") || fromLS("selectedPlan") || "";
    const backendPlan =
      sp.get("backendPlan") || fromLS("selectedPlanBackendKey") || plan || "";
    const normalizedPlan = plan || backendPlan || "basic";

    const couponFromStorage = () => {
      try {
        return (
          sessionStorage.getItem("pendingCoupon") ||
          localStorage.getItem("selectedCoupon") ||
          ""
        );
      } catch {
        return "";
      }
    };
    const coupon = (sp.get("coupon") || couponFromStorage() || "").trim();
    if (coupon) {
      try {
        sessionStorage.setItem("pendingCoupon", coupon);
        localStorage.setItem("selectedCoupon", coupon);
      } catch {}
    }

    const utm = {
      source: sp.get("utm_source") || "",
      medium: sp.get("utm_medium") || "",
      campaign: sp.get("utm_campaign") || "",
      term: sp.get("utm_term") || "",
      content: sp.get("utm_content") || "",
      referrer: document.referrer || "",
    };
    return {
      plan: normalizedPlan,
      backendPlan: backendPlan || normalizedPlan,
      queryPrice,
      accountType,
      utm,
      coupon
    };
  }, []);
}

function emailOK(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v || "");
}


// ── Free signup form (plan=free) ─────────────────────────────────────────────
function FreeSignupForm({ utm }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [refCode, setRefCode] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search || "");
      return (sp.get("ref") || sp.get("refCode") || "").trim().toUpperCase();
    } catch { return ""; }
  });

  // canSubmit removed - button only disabled when busy

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");
    if (!email.trim()) { setErr("Please enter your email."); return; }
    if (!password || password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    if (!accepted) { setErr("Please agree to the terms & conditions."); return; }

    setBusy(true);
    try {
      const { getAuth } = await import("firebase/auth");
      const { createUserWithEmailAndPassword: createUser } = await import("firebase/auth");
      const authInstance = getAuth();
      const cred = await createUser(authInstance, email.trim().toLowerCase(), password);
      const uid = cred.user.uid;

      // Send verification email
      await setDoc(doc(db, "users", uid), {
        username: email.trim().toLowerCase(),
        email: email.trim().toLowerCase(),
        name: "",
        phone: "",
        address: "",
        createdAt: serverTimestamp(),
        subscriptionStatus: "free",
        planKey: "free",
        accountTypes: { personal: true, business: false, manager: false },
        personalAccount: { active: false, plan: "free", status: "free" },
        businessAccount: { active: false, tenantId: "", role: "", billingStatus: "" },
        managerAccess: { active: false, readOnly: false },
        ...(refCode ? { refCode: refCode.toUpperCase() } : {}),
      }, { merge: true });

      // Track free signup for leads funnel
      try {
        await fetch("/api/leads/capture", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: email.trim().toLowerCase(),
            stage: "free_signup",
            plan: "free",
            utm,
          }),
        });
      } catch {}

      // Send Postmark welcome + verification email
      try {
        const idToken = await cred.user.getIdToken();
        await fetch("/api/sendFreeWelcomeEmail", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
          body: JSON.stringify({}),
        });
      } catch {}

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'registration_complete', plan: 'free' });
      window.location.href = "/";
    } catch (e2) {
      setBusy(false);
      if (e2?.code === "auth/email-already-in-use") {
        setErr("An account with this email already exists. Please log in instead.");
      } else {
        setErr(e2.message || "Could not create account. Please try again.");
      }
    }
  }

  const brand = "#f15822";
  const inputStyle = { border: "1px solid #d1d5db", borderRadius: 10, padding: "12px 14px", fontSize: 16, width: "100%", boxSizing: "border-box" };

  return (
    <div style={{ minHeight: "100vh", background: "#f6f7fb", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ background: "#fff", borderBottom: "1px solid #e8ebf3", padding: "0 24px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <a href="/"><img src="/Lastwishbox-logo.png" alt="LastWish Box" style={{ height: 38, width: "auto" }} /></a>
          <a href="/login" style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #650202", background: "#650202", color: "#fff", fontWeight: 700, fontSize: 13, textDecoration: "none" }}>Log in</a>
        </div>
      </div>

      <div style={{ padding: "32px 20px", display: "flex", justifyContent: "center" }}>
        <div style={{ width: "100%", maxWidth: 480 }}>

          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ display: "inline-block", background: "#fff0ee", color: "#980000", borderRadius: 999, padding: "6px 16px", fontSize: 13, fontWeight: 700, marginBottom: 12, border: "1px solid #f15822" }}>Free plan</div>
            <h1 style={{ margin: "0 0 8px", fontSize: 26, fontWeight: 900, color: "#2a0516" }}>Get started free</h1>
            <p style={{ margin: 0, color: "#6b7280", fontSize: 15 }}>No credit card required. Start protecting what matters in 30 seconds.</p>
          </div>

          {/* Form card */}
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 16, padding: 24, background: "#fff" }}>

            {/* Step indicator */}
            <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#980000", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14 }}>1</div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#980000" }}>Create account</span>
              </div>
              <div style={{ flex: 1, height: 2, background: "#e5e7eb", margin: "0 8px", marginBottom: 16 }} />
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#e5e7eb", color: "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14 }}>2</div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af" }}>Start using app</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} style={{ display: "grid", gap: 12 }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                required
                style={inputStyle}
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password (min 8 characters)"
                required
                style={inputStyle}
              />
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm password"
                required
                style={inputStyle}
              />

              <div>
                <input
                  type="text"
                  value={refCode}
                  onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                  placeholder="Referral code (optional)"
                  maxLength={20}
                  style={{ ...inputStyle, letterSpacing: refCode ? 2 : 0 }}
                />
                {refCode && (
                  <div style={{ fontSize: 12, color: "#065f46", marginTop: 4, paddingLeft: 2 }}>
                    ✓ Referral code applied
                  </div>
                )}
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4, paddingLeft: 2 }}>
                  If your nursing home or organisation provided a code, enter it here.
                </div>
              </div>

              {err && <div style={{ color: "#7a0b0b", background: "#fee2e2", padding: "8px 10px", borderRadius: 8, fontSize: 14 }}>{err}</div>}

              <button
                type="submit"
                disabled={busy}
                style={{
                  height: 46,
                  borderRadius: 12,
                  fontWeight: 800,
                  fontSize: 16,
                  background: busy ? "#f3f4f6" : brand,
                  color: busy ? "#9ca3af" : "#fff",
                  border: "none",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
              >
                {busy ? "Creating your account..." : "Get started free"}
              </button>

              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <span>
                  By signing up I agree to the <a href="https://lastwishbox.com/policies/" target="_blank" rel="noopener noreferrer" style={{ color: brand }}>terms &amp; conditions</a> and privacy policy.
                </span>
              </div>

              <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "4px 16px" }}>
                {["No credit card required", "Cancel anytime", "Free forever"].map((item, i) => (
                  <span key={i} style={{ fontSize: 11, color: "#6b7280" }}>{item}</span>
                ))}
              </div>
            </form>

            <p style={{ textAlign: "center", fontSize: 13, color: "#6b7280", marginTop: 16 }}>
              Already have an account? <a href="/login" style={{ color: brand, fontWeight: 700 }}>Log in</a>
            </p>
            <p style={{ textAlign: "center", fontSize: 13, color: "#6b7280", marginTop: 8 }}>
              Want unlimited access? <a href="/personal/plans" style={{ color: brand, fontWeight: 700 }}>See paid plans</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Register() {
  const { plan, backendPlan, queryPrice, accountType, utm, coupon } = usePlanParams();
  const isFree = plan === "free";
  const [refCode] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search || "");
      return (sp.get("ref") || sp.get("refCode") || "").trim().toUpperCase();
    } catch { return ""; }
  });

  const [planCatalog, setPlanCatalog] = useState(PLAN_CATALOG);

  useEffect(() => {
    if (accountType === "personal") return;

    let active = true;
    fetchPlanCatalog()
      .then((catalog) => {
        if (active && catalog?.length) setPlanCatalog(catalog);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [accountType]);

  const selectedPlan = useMemo(
    () => getPlanFromCatalog(backendPlan || plan, planCatalog),
    [backendPlan, plan, planCatalog]
  );

  const priceId =
    accountType === "personal"
      ? (queryPrice || selectedPlan?.priceId || "")
      : (selectedPlan?.priceId || "");

  const inviteToken = useMemo(() => {
    try {
      const sp = new URLSearchParams(window.location.search || "");
      return (sp.get("invite") || "").trim();
    } catch {
      return "";
    }
  }, []);

  const isInviteFlow = !!inviteToken;


  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("IE");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  const [emailExists, setEmailExists] = useState(false);
  const [emailErr, setEmailErr] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState("");


  // LEADS FUNNEL: Prefill email
  // If user comes from an email-capture step, reuse that email so they don't type it twice.
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search || "");
      const qEmail = (sp.get("email") || "").trim().toLowerCase();

      const stored = (
        (sessionStorage.getItem("lwb_lead_email") || "") ||
        (localStorage.getItem("lwb_lead_email") || "")
      ).trim().toLowerCase();

      const next = qEmail || stored;

      if (next) {
        setEmail(next);
        // persist for later pages/refresh
        sessionStorage.setItem("lwb_lead_email", next);
        localStorage.setItem("lwb_lead_email", next);
      }
    } catch {}
  }, []);

  // keep latest typed email in storage as well (helps resume)
  useEffect(() => {
    const e = (email || "").trim().toLowerCase();
    if (!e) return;
    try {
      sessionStorage.setItem("lwb_lead_email", e);
      localStorage.setItem("lwb_lead_email", e);
    } catch {}
  }, [email]);

  // LEADS FUNNEL: Capture register_opened stage (no auth)
  // Moves a captured lead forward as soon as they land on the Register page.
  useEffect(() => {
    const e = (email || "").trim().toLowerCase();
    if (!emailOK(e)) return;

    const key = `lwb_lead_stage_register_opened:${encodeURIComponent(e)}`;
    try {
      if (sessionStorage.getItem(key) === "1") return;
    } catch {}

    (async () => {
      try {
        const sp = new URLSearchParams(window.location.search || "");
        await fetch(`${FUNCTIONS_BASE}/leads/capture`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: e,
            stage: "register_opened",
            page: window.location.pathname || "/register",
            utm: {
              utm_source: utm.source || undefined,
              utm_medium: utm.medium || undefined,
              utm_campaign: utm.campaign || undefined,
              utm_term: utm.term || undefined,
              utm_content: utm.content || undefined,
              gclid: (sp.get("gclid") || "").trim() || undefined,
              fbclid: (sp.get("fbclid") || "").trim() || undefined,
            },
            referrer: document.referrer || undefined,
          }),
        });
      } catch {}
      try {
        sessionStorage.setItem(key, "1");
      } catch {}
    })();
  }, [email, utm]);

  // LEADS FUNNEL: helper to record later funnel stages (no auth) using the known email
  const captureLeadStage = (stage, extra = {}) => {
    const e = (email || "").trim().toLowerCase();
    if (!emailOK(e)) return;

    (async () => {
      try {
        const sp = new URLSearchParams(window.location.search || "");
        await fetch(`${FUNCTIONS_BASE}/leads/capture`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: e,
            stage,
            page: window.location.pathname || "/register",
            utm: {
              utm_source: utm.source || undefined,
              utm_medium: utm.medium || undefined,
              utm_campaign: utm.campaign || undefined,
              utm_term: utm.term || undefined,
              utm_content: utm.content || undefined,
              gclid: (sp.get("gclid") || "").trim() || undefined,
              fbclid: (sp.get("fbclid") || "").trim() || undefined,
            },
            referrer: document.referrer || undefined,
            ...extra,
          }),
        });
      } catch {}
    })();
  };


  const brand = "#f15822";
  const actionLinkStyle = { color: brand, fontWeight: 700, textDecoration: "underline" };

  // ✅ IMPORTANT: clear any stale cached pw when landing on Register
  useEffect(() => {
    try {
      sessionStorage.removeItem("lwb_reg_pw");
      sessionStorage.removeItem("pw:pending");
      localStorage.removeItem("lwb_reg_pw");
      localStorage.removeItem("pw:pending");
      localStorage.removeItem("lwb_reg_pw_ts");
    } catch {}
  }, []);

  // ✅ FIXED: persist pw to BOTH sessionStorage + localStorage (Stripe redirects can wipe sessionStorage)
  const persistPw = (pw) => {
    try {
      // sessionStorage (fast; may be lost on cross-site redirect)
      sessionStorage.setItem("lwb_reg_pw", pw);
      sessionStorage.setItem("pw:pending", pw);

      // localStorage fallback (survives Stripe redirect)
      localStorage.setItem("lwb_reg_pw", pw);
      localStorage.setItem("pw:pending", pw);
      localStorage.setItem("lwb_reg_pw_ts", String(Date.now()));
    } catch {}
  };

  const countryList = useMemo(
    () => [
      { code: "IE", label: "Ireland" },
      { code: "GB", label: "United Kingdom" },
      { code: "US", label: "United States" },
      { code: "AU", label: "Australia" },
      { code: "DE", label: "Germany" },
      { code: "FR", label: "France" },
      { code: "ES", label: "Spain" },
      { code: "IT", label: "Italy" },
      { code: "NL", label: "Netherlands" },
    ],
    []
  );

  useEffect(() => {
    clearManagerMode();
  }, []);

  async function checkEmail(value = email, { suppressInvalidMessage = false } = {}) {
    setEmailErr("");
    setEmailExists(false);

    const trimmed = (value || "").trim();
    if (!trimmed) return { valid: false, exists: false };

    if (!emailOK(trimmed)) {
      if (!suppressInvalidMessage) setEmailErr("Username must be an email address.");
      return { valid: false, exists: false };
    }

    try {
      const signInMethods = await fetchSignInMethodsForEmail(auth, trimmed);
      const exists = !!(signInMethods && signInMethods.length > 0);
      setEmailExists(exists);
      return { valid: true, exists };
    } catch {
      setEmailErr("Could not validate email right now.");
      return { valid: true, exists: false, error: true };
    }
  }

  const canSubmit = !busy && (email || "").trim().length > 0;

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);
    setInfo("");

    if (!isInviteFlow && !priceId) { setErr("Please choose a plan on the Plans page."); return; }
    if (!(fullName || "").trim()) { setErr("Please enter your full name."); return; }

    const trimmedEmail = (email || "").trim();
    const emailCheck = await checkEmail(trimmedEmail, { suppressInvalidMessage: true });
    if (!emailCheck.valid) { setErr("Enter a valid email."); return; }
    if (emailCheck.error) { setErr("Could not validate email right now. Please try again."); return; }
    if (emailCheck.exists) {
      setErr(
        <span>
          This email already has an account. <a style={actionLinkStyle} href="/login">Sign in</a> or{" "}
          <a style={actionLinkStyle} href="/forgot">reset your password</a>.
        </span>
      );
      return;
    }

    if (!password || password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords do not match."); return; }
    if (!acceptedTerms) { setErr("Please agree to the terms & conditions."); return; }

    setBusy(true);
    try {
      if (isInviteFlow) {
        const cred = await createUserWithEmailAndPassword(auth, trimmedEmail, password);
        const user = cred.user;
        const idToken = await user.getIdToken();

        const acceptRes = await fetch(
          "https://us-central1-lastwishbox-business.cloudfunctions.net/acceptTenantInvite",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${idToken}`
            },
            body: JSON.stringify({ token: inviteToken })
          }
        );

        const acceptData = await acceptRes.json().catch(() => ({}));
        if (!acceptRes.ok) {
          throw new Error(acceptData?.error || "Failed to accept invite");
        }

        window.location.href = "/?refresh=" + Date.now();
        return;
      }

      // keep this too (redundant, but safe)
      persistPw(password);

      // Meta Test Events: pass through ?test_event_code=TESTxxxx so server CAPI appears in Test Events
      const params = new URLSearchParams(window.location.search || "");
      const testEventCode = (params.get("test_event_code") || "").trim();

      const metadata = {
        fullName: (fullName || "").trim(),
        profile_fullName: (fullName || "").trim(),
        plan,
        backendPlan,
        profile_email: trimmedEmail,
        profile_username: trimmedEmail,
        profile_country: country || undefined,
        billing_country: country || undefined,
        utm_source: utm.source || undefined,
        utm_medium: utm.medium || undefined,
        utm_campaign: utm.campaign || undefined,
        utm_term: utm.term || undefined,
        utm_content: utm.content || undefined,
        referrer: utm.referrer || document.referrer || undefined,
        ...(refCode ? { refCode: refCode.toUpperCase() } : {}),
      };

      // LEADS FUNNEL: mark checkout started (best-effort)
      captureLeadStage("checkout_started");

      const personalSuccessUrl = window.location.origin + "/post-payment";
      const personalCancelUrl = window.location.origin + "/personal/plans";
      const defaultSuccessUrl = window.location.origin + "/post-payment";
      const defaultCancelUrl = window.location.origin + "/plans";

      const r = await fetch(`${FUNCTIONS_BASE}/createCheckoutSession`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmedEmail,
          priceId,
          mode: "subscription",
          successUrl: accountType === "personal" ? personalSuccessUrl : defaultSuccessUrl,
          cancelUrl: accountType === "personal" ? personalCancelUrl : defaultCancelUrl,
          metadata,
          test_event_code: testEventCode || undefined,
          coupon: coupon || undefined,
          utm,
        }),
      });

      if (r.status === 409) {
        setBusy(false);
        setErr(
          <span>
            That email already has an account. <a style={actionLinkStyle} href="/login">Sign in</a> or{" "}
            <a style={actionLinkStyle} href="/forgot">reset your password</a>.
          </span>
        );
        return;
      }

      if (!r.ok) {
        const t = await r.text().catch(() => "");
        throw new Error(t || `Checkout error (HTTP ${r.status})`);
      }

      let data = null;
      const ct = (r.headers && r.headers.get && r.headers.get("content-type")) || "";

      if (ct.includes("application/json")) {
        data = await r.json();
      } else {
        const raw = await r.text().catch(() => "");
        const trimmed = (raw || "").trim();
        if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
          try { data = JSON.parse(trimmed); } catch {}
        }
        if (!data) {
          // Usually means Hosting served index.html instead of the API (wrong path or missing rewrite).
          throw new Error("Checkout service returned an unexpected response. Please try again.");
        }
      }

      if (!data?.url) {
        throw new Error("Could not start checkout. Please try again.");
      }

      // LEADS FUNNEL: Stripe session created (best-effort)
      captureLeadStage("checkout_session_created");

      setInfo("Redirecting to checkout…");
      window.location.assign(data.url);
    } catch (e2) {
      setBusy(false);
      setErr(e2.message || "Could not start checkout.");
    }
  }

  const inputStyle = {
    border: "1px solid #d1d5db",
    borderRadius: 10,
    padding: "12px 14px",
    fontSize: 16,
  };

  const tooltipVisible = emailTouched && (!email || !emailOK(email));

  if (isFree) return <FreeSignupForm utm={utm} />;

  return (
    <div style={{ minHeight: "100vh", background: "#f6f7fb", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ background: "#fff", borderBottom: "1px solid #e8ebf3", padding: "0 24px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <a href="/"><img src="/Lastwishbox-logo.png" alt="LastWish Box" style={{ height: 38, width: "auto" }} /></a>
          <div style={{ display: "flex", gap: 10 }}>
            <a href="/personal/plans" style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #e8ebf3", background: "#fff", color: "#650202", fontWeight: 700, fontSize: 13, textDecoration: "none" }}>Personal plans</a>
            <a href="/login" style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #650202", background: "#650202", color: "#fff", fontWeight: 700, fontSize: 13, textDecoration: "none" }}>Log in</a>
          </div>
        </div>
      </div>
      <div style={{ padding: "32px 20px", display: "flex", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 720 }}>
        <RegisterHeader />

        <div style={{ border: "1px solid #e5e7eb", borderRadius: 16, padding: 18, marginTop: 10 }}>

          {/* Plan confirmation banner */}
          {!isInviteFlow && selectedPlan && (
            <div style={{ background: "#fff0ee", border: "1px solid #f15822", borderRadius: 10, padding: "10px 14px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#650202" }}>
                ✓ {selectedPlan.name} plan selected &nbsp;·&nbsp; {selectedPlan.priceText}{selectedPlan.cadence ? "/" + selectedPlan.cadence.replace("/ ", "") : "/year"}
              </span>
              <a href="/personal/plans" style={{ fontSize: 13, color: "#f15822", fontWeight: 700, textDecoration: "underline" }}>Change plan</a>
            </div>
          )}

          {/* Step indicator */}
          <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 20 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#980000", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14 }}>1</div>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#980000", whiteSpace: "nowrap" }}>Create account</span>
            </div>
            <div style={{ flex: 1, height: 2, background: "#e5e7eb", margin: "0 8px", marginBottom: 16 }} />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#e5e7eb", color: "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14 }}>2</div>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", whiteSpace: "nowrap" }}>Secure payment</span>
            </div>
          </div>

          <h1 style={{ fontSize: 22, fontWeight: 900, margin: "0 0 6px" }}>Create your account</h1>
          <p style={{ color: "#6b7280", margin: "0 0 14px" }}>
            {isInviteFlow
              ? "Create your account to join your company workspace."
              : "You’re one step away from protecting your family. Secure checkout powered by Stripe · Takes 2 minutes · 14-day money back guarantee"}
          </p>

          {!isInviteFlow && !priceId && (
            <p style={{ color: "#92400e", margin: "0 0 10px" }}>
              No plan selected. Please go back to <a className="underline" href="/personal/plans">Personal Plans</a> and choose one.
            </p>
          )}

          <form onSubmit={onSubmit} style={{ display: "grid", gap: 12 }}>
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ position: "relative" }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailErr("");
                    setEmailExists(false);
                    if (!emailTouched) setEmailTouched(true);
                  }}
                  onBlur={() => {
                    setEmailTouched(true);
                    checkEmail();
                  }}
                  placeholder="Username / email"
                  aria-label="Username / email"
                  autoComplete="email"
                  required
                  style={{ ...inputStyle, width: "100%" }}
                />
                {tooltipVisible && (
                  <div
                    role="tooltip"
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      marginTop: 6,
                      background: "#111827",
                      color: "#f9fafb",
                      padding: "6px 10px",
                      borderRadius: 8,
                      fontSize: 13,
                      boxShadow: "0 6px 16px rgba(15, 23, 42, 0.15)",
                      maxWidth: 260,
                      zIndex: 1,
                    }}
                  >
                    Username must be an email address.
                  </div>
                )}
              </div>
              {emailErr && <p style={{ color: "#b91c1c", fontSize: 14 }}>{emailErr}</p>}
              {!emailErr && emailExists && (
                <p style={{ color: "#92400e", fontSize: 14 }}>
                  This email already has an account. <a style={actionLinkStyle} href="/login">Sign in</a> or{" "}
                  <a style={actionLinkStyle} href="/forgot">reset password</a>.
                </p>
              )}
            </div>

            <div>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Full name"
                aria-label="Full name"
                autoComplete="name"
                style={{ ...inputStyle, width: "100%" }}
                required
              />
            </div>

            <div style={{ display: "grid", gap: 6 }}>
              <label style={{ fontWeight: 700, color: "#111827" }} htmlFor="country-select">
                Billing country
              </label>
              <select
                id="country-select"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                aria-label="Billing country"
                style={{ ...inputStyle, width: "100%", cursor: "pointer" }}
                required
              >
                {countryList.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
              <small style={{ color: "#6b7280" }}>Defaults to Ireland so VAT can be calculated correctly.</small>
            </div>



            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ position: "relative" }}>
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPassword(v);
                    persistPw(v); // ✅ persists to sessionStorage + localStorage
                  }}
                  placeholder="Password"
                  aria-label="Password"
                  autoComplete="new-password"
                  required
                  style={{ ...inputStyle, width: "100%", paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    padding: 4,
                    fontSize: 14,
                    color: "#6b7280",
                  }}
                >
                  {showPw ? "🙈" : "👁️"}
                </button>
              </div>
              <small style={{ color: "#6b7280" }}>Minimum 8 characters.</small>
            </div>

            <div>
              <div style={{ position: "relative" }}>
                <input
                  type={showPw2 ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm password"
                  aria-label="Confirm password"
                  autoComplete="new-password"
                  required
                  style={{ ...inputStyle, width: "100%", paddingRight: 40 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw2((v) => !v)}
                  aria-label={showPw2 ? "Hide password" : "Show password"}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    padding: 4,
                    fontSize: 14,
                    color: "#6b7280",
                  }}
                >
                  {showPw2 ? "🙈" : "👁️"}
                </button>
              </div>
            </div>

            <div>
              <input
                type="text"
                value={refCode}
                onChange={(e) => {}}
                placeholder="Referral code (optional)"
                maxLength={20}
                style={{ ...inputStyle, width: "100%", letterSpacing: refCode ? 2 : 0 }}
                readOnly
              />
              {refCode ? (
                <div style={{ fontSize: 12, color: "#065f46", marginTop: 4, paddingLeft: 2 }}>✓ Referral code applied</div>
              ) : (
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4, paddingLeft: 2 }}>If your nursing home or organisation provided a code, enter it here.</div>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14 }}>
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                required
                style={{ marginTop: 4 }}
                aria-label="Agree to terms and conditions"
              />
              <span>
                I agree to the <span style={{ fontWeight: 700 }}>terms &amp; conditions</span> set out in our policy
                documents.{" "}
                <a
                  className="underline"
                  href="https://lastwishbox.com/policies/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read policies
                </a>
                .
              </span>
            </div>

            {!!err && (
              <div style={{ color: "#7a0b0b", background: "#fee2e2", padding: "8px 10px", borderRadius: 8 }}>{err}</div>
            )}
            {!!info && (
              <div style={{ color: "#065f46", background: "#ecfdf5", padding: "8px 10px", borderRadius: 8 }}>{info}</div>
            )}



            {/* Change 5: Trust strip */}
            <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "4px 16px", marginBottom: 4 }}>
              {["🔒 256-bit encrypted", "✓ 14-day money back", "✕ Cancel anytime"].map((item, i) => (
                <span key={i} style={{ fontSize: 11, color: "#6b7280" }}>{item}</span>
              ))}
            </div>

            {/* Change 4: Button always active once email entered */}
            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                height: 44,
                borderRadius: 12,
                fontWeight: 800,
                background: canSubmit ? brand : "#f3f4f6",
                color: canSubmit ? "#fff" : "#9ca3af",
                border: "none",
                cursor: canSubmit ? "pointer" : "not-allowed",
              }}
            >
              {busy ? "Please wait…" : "Continue to checkout →"}
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
  );
}
